import { mapActions, mapGetters } from 'vuex'
import ChartComponent from '@/components/Chart/Chart.vue'

const INVESTOR_REPORT_SHARE_HASH = 'investor-report-2026-7e3f1b9c4a6d'

export default {
  name: 'AdminInvestorReport',
  components: {
    ChartComponent
  },
  data () {
    return {
      isLoading: {
        report: false
      },
      report: {
        summary: {},
        monthlyRevenue: []
      },
      chartVersion: 0,
      revenueChartData: null,
      revenueChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 10,
            fontColor: '#687178',
            usePointStyle: true
          }
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (tooltipItem, data) => {
              const label = data.datasets[tooltipItem.datasetIndex].label || ''
              return `${label}: ${this.formatMoney(tooltipItem.yLabel)}`
            }
          }
        },
        animation: {
          duration: 220,
          easing: 'easeOutQuart'
        },
        scales: {
          xAxes: [{
            gridLines: {
              display: false
            },
            ticks: {
              fontColor: '#687178',
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 12
            }
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              fontColor: '#687178',
              callback: value => this.formatCompactMoney(value)
            },
            gridLines: {
              color: 'rgba(23, 32, 38, 0.07)',
              zeroLineColor: 'rgba(23, 32, 38, 0.1)'
            }
          }]
        }
      },
      tableHeaders: [
        { text: 'Month', value: 'label' },
        { text: 'MRR', value: 'mrr', align: 'right' },
        { text: 'Won', value: 'wonValue', align: 'right' },
        { text: 'Lost', value: 'lostValue', align: 'right' },
        { text: 'Net', value: 'netValue', align: 'right' },
        { text: 'Net growth', value: 'netGrowthRate', align: 'right' }
      ]
    }
  },
  computed: {
    ...mapGetters([
      'user'
    ]),
    isAdmin () {
      return !!this.user?.admin
    },
    shareHash () {
      return typeof this.$route.query.hash === 'string' ? this.$route.query.hash : ''
    },
    hasValidShareHash () {
      return this.shareHash === INVESTOR_REPORT_SHARE_HASH
    },
    canViewReport () {
      return this.isAdmin || this.hasValidShareHash
    },
    summary () {
      return this.report.summary || {}
    },
    monthlyRevenue () {
      return Array.isArray(this.report.monthlyRevenue) ? this.report.monthlyRevenue : []
    },
    monthlyRevenueTableRows () {
      return this.monthlyRevenue.slice().reverse()
    }
  },
  methods: {
    ...mapActions([
      'adminInvestorReportGet'
    ]),
    async loadData () {
      if (!this.canViewReport) return

      this.isLoading.report = true
      try {
        const report = await this.adminInvestorReportGet({ hash: this.hasValidShareHash ? this.shareHash : undefined })
        this.report = {
          summary: report?.summary || {},
          monthlyRevenue: Array.isArray(report?.monthlyRevenue) ? report.monthlyRevenue : []
        }
        this.buildRevenueChart()
      } catch (err) {
        this.$store.commit('setSnackbar', {
          open: true,
          text: err?.response?.data?.message || err.message || 'Failed to load investor report',
          type: 'error'
        })
      }
      this.isLoading.report = false
    },
    buildRevenueChart () {
      if (!this.monthlyRevenue.length) {
        this.revenueChartData = null
        this.chartVersion++
        return
      }

      this.revenueChartData = {
        labels: this.monthlyRevenue.map(row => row.label),
        datasets: [
          {
            label: 'MRR',
            borderColor: '#245b7c',
            backgroundColor: 'rgba(36, 91, 124, 0.08)',
            data: this.monthlyRevenue.map(row => Number(row.mrr) || 0),
            type: 'line',
            pointBackgroundColor: '#245b7c',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: 3,
            lineTension: 0.2,
            borderWidth: 2,
            fill: false
          },
          {
            label: 'Won',
            backgroundColor: '#2f7d57',
            borderColor: '#2f7d57',
            data: this.monthlyRevenue.map(row => Number(row.wonValue) || 0),
            barPercentage: 0.72,
            categoryPercentage: 0.72
          },
          {
            label: 'Lost',
            backgroundColor: '#b64a4a',
            borderColor: '#b64a4a',
            data: this.monthlyRevenue.map(row => Number(row.lostValue) || 0),
            barPercentage: 0.72,
            categoryPercentage: 0.72
          }
        ]
      }
      this.chartVersion++
    },
    formatMoney (value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(Math.round(Number(value) || 0))
    },
    formatCompactMoney (value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'EUR',
        notation: 'compact',
        maximumFractionDigits: 1
      }).format(Number(value) || 0)
    },
    formatNumber (value) {
      return new Intl.NumberFormat('en-US').format(Number(value) || 0)
    },
    netValueClass (value) {
      const numeric = Number(value) || 0
      if (numeric > 0) return 'admin-investor-number admin-investor-number--positive'
      if (numeric < 0) return 'admin-investor-number admin-investor-number--negative'
      return 'admin-investor-number'
    },
    formatPercent (value) {
      if (value === null || typeof value === 'undefined') return '—'
      const numeric = Number(value) || 0
      const prefix = numeric > 0 ? '+' : ''
      return `${prefix}${numeric.toFixed(1)}%`
    }
  },
  async mounted () {
    document.title = 'Investor Report | ContentGecko'
    await this.loadData()
  }
}
